// W17 / M17 — Szűrőpanel.
//
// Desktopon a lista melletti panelben él, mobilon alsó lapként jön fel. A
// szűrés kliensoldali: az adat már be van töltve, a hálózatot nem terheljük
// újra minden kattintásnál.

import { t } from '../lib/i18n';
import { h, icon } from '../lib/dom';
import { avatar, priorityIconPath, statusIconPath } from '../lib/components';
import {
  priorityLabel,
  priorityMeta,
  statusLabel,
  statusMeta,
  type TaskPriority,
  type TaskStatus,
  type TaskView,
} from '../lib/model';
import type { PersonView, ProjectView } from '../lib/data';

export type TaskFilter = {
  statuses: Set<TaskStatus>;
  priorities: Set<TaskPriority>;
  assigneeIds: Set<string>;
  projectIds: Set<string>;
  /// Határidő-előbeállítás; az `custom` az egyedi tartományt használja.
  deadline: 'all' | 'today' | 'thisWeek' | 'overdue' | 'custom';
  from?: Date;
  to?: Date;
  onlyFollowed: boolean;
  /// Csak azok a feladatok, amelyeken van még fel nem ment módosítás (4.2).
  ///
  /// A terv (W17) ezt a kapcsolót mutatja, de sokáig nem volt mögötte
  /// semmi: a webes írás azonnal a Firestore-ba ment, „nem szinkronizált"
  /// állapot nem létezett. A helyi tár óta LÉTEZIK, tehát a kapcsoló is
  /// valódi adatot szűr.
  onlyPending: boolean;
};

export function emptyFilter(): TaskFilter {
  return {
    statuses: new Set(),
    priorities: new Set(),
    assigneeIds: new Set(),
    projectIds: new Set(),
    deadline: 'all',
    onlyFollowed: false,
    onlyPending: false,
  };
}

export function isFilterEmpty(filter: TaskFilter): boolean {
  return (
    filter.statuses.size === 0 &&
    filter.priorities.size === 0 &&
    filter.assigneeIds.size === 0 &&
    filter.projectIds.size === 0 &&
    filter.deadline === 'all' &&
    !filter.onlyFollowed &&
    !filter.onlyPending
  );
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/// A hét hétfővel kezdődik — magyar naptári szokás.
export function startOfWeek(date: Date): Date {
  const copy = startOfDay(date);
  const weekday = (copy.getDay() + 6) % 7;
  copy.setDate(copy.getDate() - weekday);
  return copy;
}

export function applyFilter(
  tasks: TaskView[],
  filter: TaskFilter,
  context: { userId: string; now?: Date } = { userId: '' },
): TaskView[] {
  const now = context.now ?? new Date();

  return tasks.filter((task) => {
    if (filter.statuses.size > 0 && !filter.statuses.has(task.status)) return false;
    if (filter.priorities.size > 0) {
      if (!task.priority || !filter.priorities.has(task.priority)) return false;
    }
    if (filter.assigneeIds.size > 0) {
      if (!task.assigneeIds.some((id) => filter.assigneeIds.has(id))) return false;
    }
    if (filter.projectIds.size > 0) {
      if (!task.projectId || !filter.projectIds.has(task.projectId)) return false;
    }
    if (filter.onlyFollowed && task.followerUserId !== context.userId) return false;
    if (filter.onlyPending && task.pending !== true) return false;

    if (filter.deadline !== 'all') {
      // Határidő nélküli feladat egyetlen időszak-szűrőre sem illeszkedik:
      // különben a "Ma" szűrő olyasmit is behozna, aminek nincs is határideje.
      if (!task.deadline) return false;
      const deadline = task.deadline;

      if (filter.deadline === 'today') {
        const from = startOfDay(now);
        const to = new Date(from);
        to.setDate(to.getDate() + 1);
        if (deadline < from || deadline >= to) return false;
      } else if (filter.deadline === 'thisWeek') {
        const from = startOfWeek(now);
        const to = new Date(from);
        to.setDate(to.getDate() + 7);
        if (deadline < from || deadline >= to) return false;
      } else if (filter.deadline === 'overdue') {
        if (!task.isOverdue) return false;
      } else if (filter.deadline === 'custom') {
        if (filter.from && deadline < filter.from) return false;
        if (filter.to && deadline > filter.to) return false;
      }
    }

    return true;
  });
}

/// `yyyy-mm-dd` a dátummezőnek — helyi idő szerint, nem UTC-ben.
/// A `toISOString()` UTC-re vált, ami itt egy nappal korábbi dátumot adna.
function isoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/// A hataridoszuro feliratainak KULCSAI. Kesz szoveget nem tarolhat modul
/// szintu tabla: a szotar csak a render elott erkezik meg.
const deadlineLabelKeys: Record<TaskFilter['deadline'], string> = {
  all: 'filter.anyDeadline',
  today: 'filter.today',
  thisWeek: 'filter.thisWeek',
  overdue: 'filter.overdue',
  custom: 'filter.custom',
};

export function filterPanel(options: {
  filter: TaskFilter;
  people: PersonView[];
  projects: ProjectView[];
  resultCount: number;
  onChange: (filter: TaskFilter) => void;
  onClose?: () => void;
  /// Sajat fejlec nelkul, ha a hivo mar ad egyet (pl. parbeszedablak-cim).
  showHeader?: boolean;
}): HTMLElement {
  const { filter } = options;
  const emit = () => options.onChange(filter);

  /// Mezőcsoport a terv szerint: `.flbl` címke, alatta a választók.
  function field(title: string, ...children: (HTMLElement | null)[]): HTMLElement {
    return h(
      'div',
      { class: 'field' },
      h('span', { class: 'flbl', text: title }),
      ...children,
    );
  }

  /// Kapcsolható státuszjelvény.
  ///
  /// A terv jelvényeket és prioritáscímkéket mutat, nem semleges chipeket:
  /// így a panelen ugyanaz a jelölés szerepel, mint a listában. A kiválasztást
  /// keret jelzi — a jelvények amúgy is színesek, a színbeli különbség
  /// önmagában nem lenne olvasható.
  function pill(
    on: boolean,
    className: string,
    label: string,
    iconPath: string,
    onToggle: () => void,
  ): HTMLElement {
    return h(
      'button',
      {
        type: 'button',
        class: className,
        'aria-pressed': on ? 'true' : 'false',
        style: {
          cursor: 'pointer',
          font: 'inherit',
          fontSize: '12px',
          fontWeight: '600',
          outline: on ? '2px solid var(--focus)' : 'none',
          outlineOffset: '1px',
          opacity: on ? '1' : '.6',
        },
        onClick: () => {
          onToggle();
          emit();
        },
      },
      h('span', { class: 'ic', style: { width: '12px', height: '12px' } }, icon(iconPath, 12)),
      h('span', { text: label }),
    );
  }

  /// A tervek `.sw` kapcsolója. A valódi jelölőnégyzet megmarad — csak nem
  /// látszik —, hogy a billentyűzet és a képernyőolvasó ugyanúgy kezelje.
  function switchRow(label: string, on: boolean, onToggle: (next: boolean) => void): HTMLElement {
    const input = h('input', {
      type: 'checkbox',
      checked: on ? 'checked' : undefined,
      style: { position: 'absolute', opacity: '0', width: '46px', height: '26px', margin: '0' },
      onChange: (event: Event) => {
        onToggle((event.target as HTMLInputElement).checked);
        emit();
      },
    }) as HTMLInputElement;

    return h(
      'label',
      {
        class: 'row g10',
        style: { alignItems: 'center', padding: '4px 0', cursor: 'pointer', position: 'relative' },
      },
      input,
      h('span', { class: `sw${on ? ' on' : ''}`, 'aria-hidden': 'true' }),
      h('span', { class: 'body-sm', text: label }),
    );
  }

  /// A felelos-valaszto a mezo ALATT nyilik, nem ujabb parbeszedablakban:
  /// egy parbeszedablak fole nyilo masik ablak a fokuszcsapdat is
  /// megkettozne.
  let peoplePickerOpen = false;
  const pickerHost = h('div', {
    class: 'col g4',
    style: {
      display: 'none',
      marginTop: '6px',
      maxHeight: '200px',
      overflow: 'auto',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-sm)',
      padding: '6px',
    },
  });

  function drawPicker(): void {
    pickerHost.style.display = peoplePickerOpen ? 'flex' : 'none';
    if (!peoplePickerOpen) return;
    pickerHost.replaceChildren(
      ...options.people.map((person) => {
        const on = filter.assigneeIds.has(person.id);
        return h(
          'label',
          {
            class: 'row g12',
            style: {
              padding: '6px 8px',
              borderRadius: 'var(--r-sm)',
              cursor: 'pointer',
              background: on ? 'var(--accent-soft)' : 'transparent',
            },
          },
          h('input', {
            type: 'checkbox',
            checked: on ? 'checked' : undefined,
            onChange: () => {
              if (on) filter.assigneeIds.delete(person.id);
              else filter.assigneeIds.add(person.id);
              emit();
            },
          }),
          avatar(person.fullName, 'sm', person.photoUrl),
          h('span', { class: 'body-sm', text: person.fullName }),
        );
      }),
    );
  }

  const body = h(
    'div',
    { class: 'col', style: { gap: '18px' } },

    field(
      t('task.status'),
      h(
        'div',
        { class: 'row g8 wrap' },
        ...(Object.keys(statusMeta) as TaskStatus[]).map((status) =>
          pill(
            filter.statuses.has(status),
            `badge ${statusMeta[status].cls}`,
            statusLabel(status),
            statusIconPath[status],
            () => {
              if (filter.statuses.has(status)) filter.statuses.delete(status);
              else filter.statuses.add(status);
            },
          ),
        ),
      ),
    ),

    field(
      t('task.priority'),
      h(
        'div',
        { class: 'row g8 wrap' },
        ...(Object.keys(priorityMeta) as TaskPriority[]).map((priority) =>
          pill(
            filter.priorities.has(priority),
            `prio ${priorityMeta[priority].cls}`,
            priorityLabel(priority),
            priorityIconPath[priority],
            () => {
              if (filter.priorities.has(priority)) filter.priorities.delete(priority);
              else filter.priorities.add(priority);
            },
          ),
        ),
      ),
    ),

    // A terv chip-mezot mutat a felelosoknel: a kivalasztottak bent allnak
    // chipkent, mellettuk a „+ hozzaadas…". Korabban MINDEN ember chipje
    // ott sorakozott egymas mellett — husz fonel a panel fele ez volt.
    field(
      t('task.stepAssignee'),
      h(
        'div',
        {
          class: 'input row g6 wrap',
          style: { height: 'auto', minHeight: '44px', padding: '6px 10px', alignItems: 'center' },
        },
        ...[...filter.assigneeIds].map((id) => {
          const name = options.people.find((p) => p.id === id)?.fullName ?? id;
          return h(
            'span',
            { class: 'chip on', style: { height: '28px' } },
            avatar(name, 'sm'),
            h('span', { text: name }),
            h('button', {
              class: 'x',
              type: 'button',
              'aria-label': t('filter.removeFromFilter', [name]),
              text: '×',
              style: { border: '0', background: 'none', cursor: 'pointer', font: 'inherit' },
              onClick: () => {
                filter.assigneeIds.delete(id);
                emit();
              },
            }),
          );
        }),
        h('button', {
          class: 'btn btn-ghost btn-sm',
          type: 'button',
          text: t('people.addMore'),
          onClick: () => {
            peoplePickerOpen = !peoplePickerOpen;
            drawPicker();
          },
        }),
      ),
      pickerHost,
    ),

    // A terv legordulot mutat a projektnel („Osszes projekt"), nem chipsort:
    // egy cegnel tobb tucat projekt is lehet.
    field(
      'Projekt',
      h(
        'div',
        { class: 'sel' },
        h(
          'select',
          {
            class: 'input',
            'aria-label': t('filter.projectFilter'),
            onChange: (event: Event) => {
              const value = (event.target as HTMLSelectElement).value;
              filter.projectIds.clear();
              if (value) filter.projectIds.add(value);
              emit();
            },
          },
          h('option', { value: '', text: t('filter.allProjects') }),
          ...options.projects.map((project) =>
            h('option', {
              value: project.id,
              text: project.title,
              selected: filter.projectIds.has(project.id) ? 'selected' : undefined,
            }),
          ),
        ),
      ),
    ),

    field(
      t('general.deadline'),
      h(
        'div',
        { class: 'row g6 wrap' },
        ...(Object.keys(deadlineLabelKeys) as TaskFilter['deadline'][]).map((key) =>
          h('button', {
            type: 'button',
            class: `chip${filter.deadline === key ? ' on' : ''}`,
            'aria-pressed': filter.deadline === key ? 'true' : 'false',
            text: t(deadlineLabelKeys[key]),
            style: { font: 'inherit', cursor: 'pointer' },
            onClick: () => {
              filter.deadline = key;
              if (key !== 'custom') {
                filter.from = undefined;
                filter.to = undefined;
              }
              emit();
            },
          }),
        ),
      ),
      // Az egyeni tartomany ket datummezot kap. A `from`/`to` eddig is
      // resze volt a szuronek, de megadni sehol nem lehetett oket — az
      // „Egyeni tartomany" gomb gyakorlatilag nem szurt semmire.
      filter.deadline === 'custom'
        ? h(
            'div',
            { class: 'row g6', style: { marginTop: '8px' } },
            h('input', {
              class: 'input',
              type: 'date',
              'aria-label': t('filter.rangeStart'),
              value: filter.from ? isoDate(filter.from) : '',
              onChange: (event: Event) => {
                const value = (event.target as HTMLInputElement).value;
                filter.from = value ? new Date(value) : undefined;
                emit();
              },
            }),
            h('input', {
              class: 'input',
              type: 'date',
              'aria-label': t('filter.rangeEnd'),
              value: filter.to ? isoDate(filter.to) : '',
              onChange: (event: Event) => {
                const value = (event.target as HTMLInputElement).value;
                filter.to = value ? new Date(value) : undefined;
                emit();
              },
            }),
          )
        : null,
    ),

    field(
      t('task.colOther'),
      // A terv sorrendje: eloszor a kovetes, utana a szinkronizalas.
      switchRow(t('task.onlyFollowed'), filter.onlyFollowed, (next) => {
        filter.onlyFollowed = next;
      }),
      switchRow(t('task.onlyPending'), filter.onlyPending, (next) => {
        filter.onlyPending = next;
      }),
    ),
  );

  drawPicker();

  return h(
    'div',
    { class: 'col', style: { gap: '0', height: '100%' } },
    options.showHeader === false
      ? null
      : h(
          'div',
          {
            class: 'row between',
            style: { padding: '16px 20px', borderBottom: '1px solid var(--border)' },
          },
          h('h2', { class: 'h3', text: t('filter.shortTitle') }),
          options.onClose
            ? h('button', {
                class: 'btn btn-ghost btn-icon btn-sm',
                type: 'button',
                'aria-label': t('filter.close'),
                text: '✕',
                onClick: options.onClose,
              })
            : null,
        ),
    h('div', { style: { flexGrow: '1', overflow: 'auto', padding: '20px' } }, body),
    // A terv lablece: balra a torles, jobbra a talalatszammal felirt gomb.
    // A szures elve fut, ezert ez a gomb bezar — nem „alkalmaz".
    h(
      'div',
      {
        class: 'row g8',
        style: { padding: '16px 20px', borderTop: '1px solid var(--border)', alignItems: 'center' },
      },
      h('button', {
        class: 'btn btn-ghost',
        type: 'button',
        text: t('filter.clear'),
        disabled: isFilterEmpty(filter) ? 'disabled' : undefined,
        onClick: () => {
          const cleared = emptyFilter();
          filter.statuses = cleared.statuses;
          filter.priorities = cleared.priorities;
          filter.assigneeIds = cleared.assigneeIds;
          filter.projectIds = cleared.projectIds;
          filter.deadline = cleared.deadline;
          filter.from = undefined;
          filter.to = undefined;
          filter.onlyFollowed = cleared.onlyFollowed;
          emit();
        },
      }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        style: { marginLeft: 'auto' },
        text: t('filter.showResults', [String(options.resultCount)]),
        onClick: () => options.onClose?.(),
      }),
    ),
  );
}
